/**
 * Keeps the add-expense form across a page refresh (localStorage), cleared after a successful
 * save or when the spender chooses Leave.
 *
 * Photos are not kept: localStorage holds only text and photos are too large for it. They
 * will move to IndexedDB together with the offline queue.
 */
import { isEntryDate } from '../lib/dates.ts';
import { isUnit } from '../lib/units.ts';
import { REASONS, emptyDraft, type Draft, type ReasonKey } from './draft.ts';

const VERSION = 1;

/** One draft per signed-in user, so a shared phone never shows someone else's entry. */
export function draftKey(userId: string): string {
  return `expense.draft.v${VERSION}.${userId}`;
}

type Stored = Omit<Draft, 'photos'>;

function hasContent(d: Draft): boolean {
  return Boolean(d.categoryId || d.itemName || d.amount || d.noReceipt || d.reasonText);
}

export function saveDraft(storage: Storage | undefined, userId: string, d: Draft): void {
  if (!storage) return;
  try {
    if (!hasContent(d) || d.correctsId) {
      // Corrections reload from the server; an empty form needs nothing kept.
      storage.removeItem(draftKey(userId));
      return;
    }
    const { photos: _photos, ...rest } = d;
    storage.setItem(draftKey(userId), JSON.stringify(rest satisfies Stored));
  } catch {
    /* storage full or blocked (private mode): the form still works, just isn't kept */
  }
}

export function clearDraft(storage: Storage | undefined, userId: string): void {
  try {
    storage?.removeItem(draftKey(userId));
  } catch {
    /* ignore */
  }
}

/**
 * The saved draft for this user, or null if there is none, it's unreadable, or it belongs to a
 * different wallet than the one being opened. A date that is no longer allowed (more than
 * 7 days ahead) or unreadable is reset to today; any past date is kept.
 */
export function loadDraft(storage: Storage | undefined, userId: string, today: string, entityId: string | null): Draft | null {
  let raw: string | null = null;
  try {
    raw = storage?.getItem(draftKey(userId)) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  let s: Partial<Stored>;
  try {
    s = JSON.parse(raw) as Partial<Stored>;
  } catch {
    return null;
  }
  if (typeof s.entityId !== 'string' || (entityId && s.entityId !== entityId)) return null;
  const base = emptyDraft(s.entityId, today);
  return {
    ...base,
    categoryId: typeof s.categoryId === 'string' ? s.categoryId : null,
    itemName: typeof s.itemName === 'string' ? s.itemName.slice(0, 60) : '',
    quantity: typeof s.quantity === 'number' && s.quantity >= 0 ? s.quantity : 0,
    unit: isUnit(s.unit) ? s.unit : null,
    amount: typeof s.amount === 'string' && /^\d{0,6}$/.test(s.amount) ? s.amount : '',
    noReceipt: s.noReceipt === true,
    reason: REASONS.includes(s.reason as ReasonKey) ? (s.reason as ReasonKey) : null,
    reasonText: typeof s.reasonText === 'string' ? s.reasonText.slice(0, 200) : '',
    txnDate: isEntryDate(s.txnDate, today) ? s.txnDate : today,
    paidBy: typeof s.paidBy === 'string' ? s.paidBy : null,
  };
}
